import ResponsiveSelect from "../components/ResponsiveSelect";
import {handleGraphQLError} from "../utils/graphqlErrorHandler";
import Slide from "@mui/material/Slide";
import useTriStateSort from "../hooks/useTriStateSort";
import React, {useEffect, useMemo, useState} from "react";
import PageHeader from "../components/PageHeader";
import PeopleIcon from "@mui/icons-material/People";
import {formatToLocalTime} from "../utils/dateUtils";
import SearchField from "../components/SearchField";
import SuccessDialog from "../components/SuccessDialog";
import {Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, Typography, useMediaQuery} from "@mui/material";
import {gql, useMutation, useQuery} from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import {useTranslation} from "react-i18next";
import EllipsisCell from "../components/EllipsisCell";
import displayValue from "../utils/displayValue";
import TablePaginationActions from "../components/TablePaginationActions";

const SlideUp = (props) => <Slide {...props} direction="up"/>;


const PAGINATE_RESELLER_USERS = gql`
    query PaginateResellerUsers(
        $pagination: Pagination!
        $sortBy: [SortBy!]
        $criteria: ResellerUserCriteria
    ) {
        paginateResellerUser(
            pagination: $pagination
            sortBy: $sortBy
            criteria: $criteria
        ) {
            pageInfo {
                totalElements
                totalPages
                numberOfElements
                pageNumber
                pageSize
            }
            content {
                id
                email
                firstName
                lastName
                updatedTimestamp
                deleted
                reseller {
                    id
                    name
                    updatedTimestamp
                }
            }
        }
    }
`;


const FILTER_RESELLERS = gql`
    query FilterReseller {
        filterReseller(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;


const CREATE_RESELLER_USER = gql`
    mutation CreateResellerUser($input: ResellerUserInput!) {
        createResellerUser(input: $input) {
            id
            email
            firstName
            lastName
            deleted
            reseller { id name }
            defaultPassword
        }
    }
`;

const UPDATE_RESELLER_USER = gql`
    mutation UpdateResellerUser($id: ID!, $input: ResellerUserInput!) {
        updateResellerUser(id: $id, input: $input) {
            id
            firstName
            lastName
            email
            reseller { id name }
            deleted
        }
    }
`;

const DELETE_RESELLER_USER = gql`
    mutation DeleteResellerUser($id: ID!) {
        deleteResellerUser(id: $id)
    }
`;

const ResellerUsers = () => {
    const {t} = useTranslation();
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const showResellerColumn = role !== "RESELLER";
    const emptyStateColumns = showResellerColumn ? 5 : 4;


    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);
    const {sortFields, handleSort, getSortProps} = useTriStateSort(
        (updated) => {
            refetch({...variables, sortBy: updated});
        }
    );


    const [nameFilter, setNameFilter] = useState("");
    const [emailFilter, setEmailFilter] = useState("");
    const [resellerFilter, setResellerFilter] = useState("");
    const [debouncedFilters, setDebouncedFilters] = useState({
        name: "",
        email: "",
        reseller: "",
    });

    const [createdUserInfo, setCreatedUserInfo] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);


    const [createForm, setCreateForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        reseller: "",
    });
    const [editForm, setEditForm] = useState({
        id: "",
        firstName: "",
        lastName: "",
        email: "",
        reseller: "",
    });
    const [deleteId, setDeleteId] = useState("");


    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "success"});
    const [createError, setCreateError] = useState("");
    const [createApiError, setCreateApiError] = useState({firstName: "", lastName: "", email: "", general: ""});
    const [editApiError, setEditApiError] = useState({firstName: "", lastName: "", email: "", general: ""});
    const [editError, setEditError] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const isSmall = useMediaQuery("(max-width:600px)");


    const variables = useMemo(() => {
        const criteria = {
            deleted: false,
            ...(debouncedFilters.name ? {name: debouncedFilters.name} : {}),
            ...(debouncedFilters.email ? {email: debouncedFilters.email} : {}),
            ...(debouncedFilters.reseller ? {reseller: debouncedFilters.reseller} : {}),
        };

        return {
            pagination: {page, itemsPerPage: rowsPerPage},
            sortBy: sortFields,
            criteria,
        };
    }, [page, rowsPerPage, sortFields, debouncedFilters]);

    const {data, loading, error, refetch} = useQuery(PAGINATE_RESELLER_USERS, {
        variables,
        fetchPolicy: "no-cache",
    });

    const {data: resellerData} = useQuery(FILTER_RESELLERS, {
        skip: role === "RESELLER",
        fetchPolicy: "no-cache",
    });

    const [createResellerUser, {loading: creating}] = useMutation(CREATE_RESELLER_USER);
    const [updateResellerUser, {loading: updating}] = useMutation(UPDATE_RESELLER_USER);
    const [deleteResellerUser, {loading: deleting}] = useMutation(DELETE_RESELLER_USER);

    const pageInfo = data?.paginateResellerUser?.pageInfo;
    const rows = data?.paginateResellerUser?.content ?? [];

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRpp(parseInt(e.target.value, 10));
        setPage(0);
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setDebouncedFilters({
                name: nameFilter.trim(),
                email: emailFilter.trim(),
                reseller: resellerFilter || "",
            });
            setPage(0);
        }
    };
    const applyFilters = () => {
        setDebouncedFilters({
            name: nameFilter.trim(),
            email: emailFilter.trim(),
            reseller: resellerFilter || "",
        });
        setPage(0);
    };


    const openEdit = (row) => {
        setEditForm({
            id: row.id,
            firstName: row.firstName || "",
            lastName: row.lastName || "",
            email: row.email || "",
            reseller: row.reseller?.id || "",
        });
        setEditOpen(true);
    };

    const openDelete = (row) => {
        setDeleteId(row.id);
        setEditForm((prev) => ({...prev, firstName: row.firstName, lastName: row.lastName}));
        setDeleteOpen(true);
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        setCreateError("");
        setCreateApiError({email: "", firstName: "", lastName: "", general: ""});
        setCreateForm({firstName: "", lastName: "", email: "", reseller: ""});
    };

    const handleCloseEdit = () => {
        setEditOpen(false);
        setEditApiError({email: "", firstName: "", lastName: "", general: ""});
        setEditError("");
    };

    const handleCloseDelete = () => {
        setDeleteOpen(false);
        setDeleteError("");
    };

    const doCreate = async () => {
        setCreateApiError({email: "", firstName: "", lastName: "", general: ""});
        if (!createForm.firstName.trim() || !createForm.lastName.trim() || !createForm.email.trim()) {
            setCreateError(t("validation.requiredAll"));
            return;
        }
        if (!isValidEmail(createForm.email)) {
            setCreateError(t("validation.invalidEmail"));
            return;
        }
        if (role !== "RESELLER" && !createForm.reseller.trim()) {
            setCreateError(t("resellerUserPage.validation.selectReseller"));
            return;
        }

        const input = {
            firstName: createForm.firstName,
            lastName: createForm.lastName,
            email: createForm.email,
            ...(role !== "RESELLER" && createForm.reseller ? {reseller: createForm.reseller} : {}),
        };
        try {
            setCreateError("");

            const {data} = await createResellerUser({variables: {input}});
            const created = data?.createResellerUser;
            handleCloseCreate();
            refetch();

            if (created) {
                setCreatedUserInfo({
                    title: t("resellerUserPage.success.title"),
                    fields: [
                        {label: t("resellerUserPage.success.fields.name"), value: `${created.firstName} ${created.lastName}`},
                        {label: t("resellerUserPage.success.fields.email"), value: created.email},
                        {label: t("resellerUserPage.success.fields.reseller"), value: displayValue(created.reseller?.name)},
                        {label: t("resellerUserPage.success.fields.defaultPassword"), value: created.defaultPassword, color: "green"},
                    ],
                    copyText: `${created.defaultPassword}`,
                    showAlert: true,
                    alertText: t("resellerUserPage.success.alertText"),
                });
            }

        } catch (err) {
            handleGraphQLError(err, setCreateApiError, t);
        }
    };

    const doUpdate = async () => {
        setEditApiError({email: "", firstName: "", lastName: "", general: ""});
        const {id, ...rest} = editForm;
        if (!rest.firstName.trim() || !rest.lastName.trim() || !rest.email.trim()) {
            setEditError(t("validation.requiredAll"));
            return;
        }
        if (!isValidEmail(rest.email)) {
            setEditError(t("validation.invalidEmail"));
            return;
        }
        const input = {
            firstName: rest.firstName,
            lastName: rest.lastName,
            email: rest.email,
            ...(role !== "RESELLER" && rest.reseller ? {reseller: rest.reseller} : {}),
        };
        try {
            setEditError("");
            await updateResellerUser({variables: {id, input}});
            handleCloseEdit();
            setSnackbar({open: true, message: t("resellerUserPage.messages.updated"), severity: "success"});
            refetch();
        } catch (err) {
            handleGraphQLError(err, setEditApiError, t);
        }
    };

    const doDelete = async () => {

        try {
            const userId =
                localStorage.getItem("id") ||
                sessionStorage.getItem("id");
            if (userId === deleteId) {
                setDeleteError(t("common.cannotDeleteYourself") || "You cannot delete your own account.");
                return;
            }
            setDeleteError("");
            await deleteResellerUser({variables: {id: deleteId}});
            handleCloseDelete();
            setSnackbar({open: true, message: t("resellerUserPage.messages.deleted"), severity: "success"});
            refetch();
        } catch (err) {
            const msg = err?.graphQLErrors?.[0]?.message || t("common.deleteFailed");
            setDeleteError(msg);
        }
    };

    useEffect(() => {
        if (!createOpen) setCreateError("");
    }, [createOpen]);
    useEffect(() => {
        if (!editOpen) setEditError("");
    }, [editOpen]);
    useEffect(() => {
        if (!deleteOpen) setDeleteError("");
    }, [deleteOpen]);

    const onResetFilter = () => {
        setNameFilter("");
        setEmailFilter("");

        setResellerFilter("");
        setDebouncedFilters({name: "", email: "", reseller: ""});
        setPage(0);
    };

    const onRefresh = () => refetch();

    return (
        <Box>
            {/* <Typography variant="h5" gutterBottom> {t("resellerUserPage.title")}</Typography> */}
            <PageHeader
                title={t("resellerUserPage.title")}
                subtitle={t("resellerUserPage.subtitle")}
                icon={<PeopleIcon/>}
            />

            <Paper elevation={3} sx={{borderRadius: 2, overflow: "hidden"}}>
                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        flexWrap: "wrap",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                    }}
                >

                    <SearchField
                        label={"userPage.searchNamePlaceholder"}
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("userPage.searchNamePlaceholder")}
                        isSmall={isSmall}
                    />


                    <SearchField
                        label={"userPage.searchEmailPlaceholder"}
                        value={emailFilter}
                        onChange={(e) => setEmailFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("userPage.searchEmailPlaceholder")}
                        isSmall={isSmall}
                    />


                    {role !== "RESELLER" && (

                        <ResponsiveSelect
                            label={t("common.reseller")}
                            value={resellerFilter}
                            onChange={(e) => setResellerFilter(e.target.value)}
                            onClear={() => setResellerFilter("")}
                            data={resellerData}
                            dataKey="filterReseller"
                            t={t}
                            isSmall={isSmall}
                        />
                        // <TextField
                        //   select
                        //   size="small"
                        //   variant="outlined"
                        //   value={resellerFilter}
                        //   onChange={(e) => setResellerFilter(e.target.value)}
                        //   onKeyDown={handleSearchKeyDown}
                        //   label={t("common.reseller")}
                        //   sx={{ minWidth: isSmall ? "100%" : 220 }}
                        // >
                        //   <MenuItem value="">{t("common.all")}</MenuItem>
                        //   {(resellerData?.filterReseller ?? []).map((r) => (
                        //     <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                        //   ))}
                        // </TextField>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon/>}
                        onClick={applyFilters}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.search")}
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon/>}
                        onClick={onResetFilter}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.clear")}
                    </Button>

                    <Box sx={{flex: 1}}/>


                    <Tooltip title={t("common.refresh")}>
            <span>
              <IconButton onClick={onRefresh} aria-label="refresh">
                <RefreshIcon/>
              </IconButton>
            </span>
                    </Tooltip>

                    <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setCreateOpen(true)}>
                        {t("userPage.createButton")}
                    </Button>
                </Toolbar>

                {loading ? (
                    <Box sx={{p: 4, textAlign: "center"}}>
                        <CircularProgress/>
                    </Box>
                ) : error ? (
                    <Box sx={{p: 2}}>
                        <Alert severity="error">{t("common.error")}: {error.message}</Alert>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{maxHeight: 520}}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>

                                        <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary"}}>
                                            <TableSortLabel
                                                {...getSortProps("firstName")}
                                                onClick={() => handleSort("firstName")}
                                            >
                                                {t("userPage.table.name")}
                                            </TableSortLabel>
                                        </EllipsisCell>


                                        <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary"}}>
                                            <TableSortLabel
                                                {...getSortProps("email")}
                                                onClick={() => handleSort("email")}
                                            >
                                                {t("userPage.table.email")}
                                            </TableSortLabel>
                                        </EllipsisCell>

                                        {role !== "RESELLER" && (
                                            <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary"}}>
                                                <TableSortLabel
                                                    {...getSortProps("reseller")}

                                                    onClick={() => handleSort("reseller")}
                                                >
                                                    {t("common.reseller")}
                                                </TableSortLabel>
                                            </EllipsisCell>
                                        )}

                                        <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary", width: "1%", whiteSpace: "nowrap"}}>
                                            <TableSortLabel
                                                {...getSortProps("updatedTimestamp")}
                                                onClick={() => handleSort("updatedTimestamp")}
                                            >
                                                {t("userPage.table.updated")}
                                            </TableSortLabel>
                                        </EllipsisCell>

                                        <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary", width: "1%", whiteSpace: "nowrap"}}>
                                            {t("userPage.table.actions")}
                                        </EllipsisCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <EllipsisCell colSpan={emptyStateColumns} align="center">{t("userPage.noRecords")}</EllipsisCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((r) => (
                                            <TableRow
                                                key={r.id}
                                                hover
                                                sx={{
                                                    "&:nth-of-type(even)": {
                                                        bgcolor: "grey.50"
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "action.hover"
                                                    }
                                                }}
                                            >
                                                <EllipsisCell>
                                                    {`${r.firstName ?? ""} ${r.lastName ?? ""}`.trim()}
                                                </EllipsisCell>

                                                <EllipsisCell>
                                                    {displayValue(r.email)}
                                                </EllipsisCell>

                                                {role !== "RESELLER" && (
                                                    <EllipsisCell>
                                                        {displayValue(r.reseller?.name)}
                                                    </EllipsisCell>
                                                )}
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    {r.updatedTimestamp ? formatToLocalTime(r.updatedTimestamp) : "-"}
                                                </EllipsisCell>
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    <Tooltip title={t("common.update")}>
                                                        <IconButton size="small" onClick={() => openEdit(r)}>
                                                            <EditIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton size="small" color="error" onClick={() => openDelete(r)}>
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </EllipsisCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            labelRowsPerPage={t("common.rowsPerPage")}
                            component="div"
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={pageInfo?.totalElements ?? 0}
                            rowsPerPage={pageInfo?.pageSize ?? rowsPerPage}
                            page={pageInfo?.pageNumber ?? page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </>
                )}
            </Paper>


            <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("userPage.createTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {createApiError.general && <Alert severity="error" sx={{mb: 2}}>{createApiError.general}</Alert>}
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("userPage.fields.firstName")}
                            value={createForm.firstName}
                            onChange={(e) => setCreateForm({...createForm, firstName: e.target.value})}
                            error={!!createApiError.firstName || (createError && !createForm.firstName.trim())}
                            helperText={
                                createApiError.firstName ||
                                (createError && !createForm.firstName.trim() ? t("validation.requiredField") : "")
                            }
                            autoFocus
                        />
                        <TextField
                            label={t("userPage.fields.lastName")}
                            value={createForm.lastName}
                            onChange={(e) => setCreateForm({...createForm, lastName: e.target.value})}
                            error={!!createApiError.lastName || (createError && !createForm.lastName.trim())}
                            helperText={
                                createApiError.lastName ||
                                (createError && !createForm.lastName.trim() ? t("validation.requiredField") : "")
                            }
                        />
                        <TextField
                            label={t("userPage.fields.email")}
                            type="email"
                            value={createForm.email}
                            onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                            error={
                                !!createApiError.email ||
                                (createError && (!createForm.email.trim() || !isValidEmail(createForm.email)))
                            }
                            helperText={
                                createApiError.email ||
                                (createError && !createForm.email.trim()
                                    ? t("validation.requiredField")
                                    : createError && !isValidEmail(createForm.email)
                                        ? t("validation.invalidEmail")
                                        : "")
                            }
                        />


                        {role !== "RESELLER" && (


                            <ResponsiveSelect
                                label={t("common.reseller")}
                                value={createForm.reseller}
                                onChange={(e) => setCreateForm({...createForm, reseller: e.target.value})}
                                data={resellerData}
                                dataKey="filterReseller"
                                fullWidth={true}
                                error={!!createError && !createForm.reseller.trim()}
                                helperText={
                                    createError && !createForm.reseller.trim()
                                        ? t("validation.resellerRequired")
                                        : ""
                                }
                            />
                            // <TextField
                            //   select
                            //   label={t("common.reseller")}
                            //   value={createForm.reseller}
                            //   onChange={(e) => setCreateForm({ ...createForm, reseller: e.target.value })}
                            //   error={!!createError && !createForm.reseller.trim()}
                            //   helperText={
                            //     createError && !createForm.reseller.trim()
                            //       ? t("validation.resellerRequired")
                            //       : ""
                            //   }
                            // >
                            //   {(resellerData?.filterReseller ?? []).map((r) => (
                            //     <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                            //   ))}
                            // </TextField>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreate}>{t("common.cancel")}</Button>
                    <Button onClick={doCreate} disabled={creating} aria-busy={creating} variant="contained">
                        {creating ? <CircularProgress size={20}/> : t("common.create")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("userPage.updateTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {editApiError.general && <Alert severity="error" sx={{mb: 2}}>{editApiError.general}</Alert>}
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("userPage.fields.firstName")}
                            value={editForm.firstName}
                            onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                            error={!!editApiError.firstName || (editError && !editForm.firstName.trim())}
                            helperText={
                                editApiError.firstName ||
                                (editError && !editForm.firstName.trim() ? t("validation.requiredField") : "")
                            }
                            autoFocus
                        />
                        <TextField
                            label={t("userPage.fields.lastName")}
                            value={editForm.lastName}
                            onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                            error={!!editApiError.lastName || (editError && !editForm.lastName.trim())}
                            helperText={
                                editApiError.lastName ||
                                (editError && !editForm.lastName.trim() ? t("validation.requiredField") : "")
                            }
                        />
                        <TextField
                            label={t("userPage.fields.email")}
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                            error={
                                !!editApiError.email ||
                                (editError && (!editForm.email.trim() || !isValidEmail(editForm.email)))
                            }
                            helperText={
                                editApiError.email ||
                                (editError && !editForm.email.trim()
                                    ? t("validation.requiredField")
                                    : editError && !isValidEmail(editForm.email)
                                        ? t("validation.invalidEmail")
                                        : "")
                            }
                        />

                        {role !== "RESELLER" && (
                            <ResponsiveSelect
                                label={t("common.reseller")}
                                value={editForm.reseller}
                                onChange={(e) => setEditForm({...editForm, reseller: e.target.value})}
                                data={resellerData}
                                dataKey="filterReseller"
                                fullWidth={true}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit}>{t("common.cancel")}</Button>
                    <Button onClick={doUpdate} disabled={updating} aria-busy={updating} variant="contained">
                        {updating ? <CircularProgress size={20}/> : t("common.update")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={deleteOpen} onClose={handleCloseDelete}>
                <DialogTitle sx={{fontWeight: "bold"}}>{t("userPage.deleteConfirmTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {deleteError && <Alert severity="error" sx={{mb: 2}}>{deleteError}</Alert>}
                <DialogContent>
                    <Typography variant="h6" sx={{color: "#d32f2f", fontWeight: "bold", mt: 1}}>
                        {`${editForm.firstName} ${editForm.lastName}`}
                    </Typography>
                    <Typography>{t("userPage.deleteConfirmMessage")}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>{t("common.cancel")}</Button>
                    <Button onClick={doDelete} color="error" disabled={deleting} aria-busy={deleting} variant="contained">
                        {deleting ? <CircularProgress size={20}/> : t("common.delete")}
                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessDialog
                open={!!createdUserInfo}
                onClose={() => setCreatedUserInfo(null)}
                info={createdUserInfo}
            />

            <Snackbar
                TransitionComponent={SlideUp}
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                sx={{
                    "& .MuiAlert-root": {
                        borderRadius: 2,
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                        fontWeight: 500,
                        minWidth: 260,
                        justifyContent: "center",
                    },
                }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ResellerUsers;
